import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import { trackBeginCheckout, trackPurchase, trackPaymentFailed } from '../utils/analytics';

interface Props {
  packageType: 'monthly' | 'yearly' | 'fouryear';
  packageName: string;
  voucherCode?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const PACKAGES: Record<string, { name: string; price: number }> = {
  monthly: { name: 'Premium 1 Bulan', price: 20000 },
  yearly: { name: 'Premium Setahun', price: 50000 },
  fouryear: { name: 'Premium 4 Tahun', price: 200000 },
};

export default function QRISPaymentModal({ packageType, packageName, voucherCode, onClose, onSuccess }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [paymentData, setPaymentData] = useState<{
    orderId: string;
    qrisUrl: string;
    qrisString: string;
    totalAmount: number;
    discount: number;
    expiresAt: string;
  } | null>(null);
  const [status, setStatus] = useState<'pending' | 'paid' | 'expired' | 'failed'>('pending');
  const [countdown, setCountdown] = useState('');
  const [successCountdown, setSuccessCountdown] = useState(5);

  // Create payment on mount
  useEffect(() => {
    createPayment();
  }, []);

  // Poll status every 5 seconds
  useEffect(() => {
    if (!paymentData || status !== 'pending') return;

    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [paymentData, status]);

  // Countdown timer
  useEffect(() => {
    if (!paymentData?.expiresAt || status !== 'pending') return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const expires = new Date(paymentData.expiresAt).getTime();
      const diff = expires - now;

      if (diff <= 0) {
        setStatus('expired');
        setCountdown('00:00:00');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const secs = Math.floor((diff % (1000 * 60)) / 1000);
      setCountdown(
        `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [paymentData?.expiresAt, status]);

  const createPayment = async () => {
    try {
      setLoading(true);
      setError('');
      setStatus('pending'); // Reset status for retry
      setPaymentData(null); // Clear old payment data

      const res = await fetch('/api/payments/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ packageType, voucherCode }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Gagal membuat pembayaran');
        return;
      }

      setPaymentData(data);
      
      // Track checkout initiation
      trackBeginCheckout(packageType, data.totalAmount, data.discount, voucherCode);
    } catch (err) {
      setError('Terjadi kesalahan jaringan');
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async () => {
    if (!paymentData) return;

    try {
      const res = await fetch(`/api/payments/status/${paymentData.orderId}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (data.status === 'paid') {
        setStatus('paid');
        
        // Track successful purchase - CONVERSION EVENT
        if (paymentData) {
          trackPurchase(
            paymentData.orderId,
            packageType,
            paymentData.totalAmount,
            paymentData.discount,
            voucherCode
          );
        }
        
        // Start 5 second countdown then refresh
        let count = 5;
        setSuccessCountdown(count);
        const countdownInterval = setInterval(() => {
          count--;
          setSuccessCountdown(count);
          if (count <= 0) {
            clearInterval(countdownInterval);
            window.location.reload();
          }
        }, 1000);
      } else if (data.status === 'expired') {
        setStatus('expired');
        trackPaymentFailed(packageType, 'expired');
      } else if (data.status === 'failed') {
        setStatus('failed');
        trackPaymentFailed(packageType, 'failed');
      }
    } catch (err) {
      console.error('Status check failed:', err);
    }
  };

  const pkg = PACKAGES[packageType];

  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <Handle />

        {loading ? (
          <StateBox>
            <Spinner />
            <StateText>Menyiapkan pembayaran...</StateText>
          </StateBox>
        ) : error ? (
          <StateBox>
            <StateIcon>😢</StateIcon>
            <StateTitle>Gagal</StateTitle>
            <StateText>{error}</StateText>
            <RetryButton onClick={createPayment}>Coba Lagi</RetryButton>
          </StateBox>
        ) : status === 'paid' ? (
          <StateBox>
            <StateIcon>🎉</StateIcon>
            <StateTitle style={{ color: '#10b981' }}>Pembayaran Berhasil!</StateTitle>
            <StateText>Langganan Premium kamu sudah aktif</StateText>
            <CountdownRefresh>Halaman akan refresh dalam {successCountdown} detik...</CountdownRefresh>
          </StateBox>
        ) : status === 'expired' ? (
          <StateBox>
            <StateIcon>⏰</StateIcon>
            <StateTitle>Pembayaran Expired</StateTitle>
            <StateText>Waktu pembayaran habis</StateText>
            <RetryButton onClick={createPayment}>Buat Pembayaran Baru</RetryButton>
          </StateBox>
        ) : status === 'failed' ? (
          <StateBox>
            <StateIcon>❌</StateIcon>
            <StateTitle>Pembayaran Gagal</StateTitle>
            <RetryButton onClick={createPayment}>Coba Lagi</RetryButton>
          </StateBox>
        ) : (
          <>
            <Title>Scan QRIS</Title>
            <Subtitle>Gunakan aplikasi e-wallet atau mobile banking</Subtitle>

            <QRISBox>
              {paymentData?.qrisString ? (
                <QRCodeSVG
                  value={paymentData.qrisString}
                  size={220}
                  level="M"
                  includeMargin={true}
                />
              ) : paymentData?.qrisUrl ? (
                <QRISImage src={paymentData.qrisUrl} alt="QRIS" />
              ) : (
                <QRISPlaceholder>QR Code tidak tersedia</QRISPlaceholder>
              )}
            </QRISBox>

            <AmountBox>
              <AmountLabel>Total Pembayaran</AmountLabel>
              <AmountValue>Rp {paymentData?.totalAmount.toLocaleString('id-ID')}</AmountValue>
              {paymentData?.discount ? (
                <DiscountInfo>
                  Hemat Rp {paymentData.discount.toLocaleString('id-ID')}
                </DiscountInfo>
              ) : null}
            </AmountBox>

            <InfoBox>
              <InfoIcon>💡</InfoIcon>
              <InfoText>
                Pembayaran otomatis terverifikasi dalam 1-2 menit setelah transfer
              </InfoText>
            </InfoBox>

            <SupportedApps>
              <SupportedLabel>Didukung oleh:</SupportedLabel>
              <AppList>
                <AppBadge>GoPay</AppBadge>
                <AppBadge>OVO</AppBadge>
                <AppBadge>DANA</AppBadge>
                <AppBadge>ShopeePay</AppBadge>
                <AppBadge>LinkAja</AppBadge>
              </AppList>
              <BankingNote>dan aplikasi perbankan lainnya</BankingNote>
            </SupportedApps>

            <StatusBar>
              <PulsingDot />
              <StatusText>Menunggu pembayaran...</StatusText>
              <CountdownText>{countdown}</CountdownText>
            </StatusBar>

            <OrderInfo>
              Order ID: <strong>{paymentData?.orderId}</strong>
            </OrderInfo>
          </>
        )}

        <CloseButton onClick={onClose}>
          {status === 'paid' ? 'Selesai' : 'Tutup'}
        </CloseButton>
      </Sheet>
    </Overlay>
  );
}

/* ── Styled Components ── */

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 200;
`;

const Sheet = styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 20px 24px 34px;
  width: 100%;
  max-width: 512px;
  max-height: 90vh;
  overflow-y: auto;
  font-family: 'Plus Jakarta Sans', sans-serif;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const Handle = styled.div`
  width: 40px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 0 auto 20px;
`;

const Title = styled.h3`
  font-size: 22px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 6px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px;
  text-align: center;
`;

const QRISBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: #f8fafc;
  border-radius: 16px;
  margin-bottom: 16px;
`;

const QRISImage = styled.img`
  width: 220px;
  height: 220px;
  object-fit: contain;
`;

const QRISPlaceholder = styled.div`
  width: 220px;
  height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 14px;
`;

const AmountBox = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;

const AmountLabel = styled.div`
  font-size: 13px;
  color: #64748b;
  margin-bottom: 4px;
`;

const AmountValue = styled.div`
  font-size: 28px;
  font-weight: 800;
  color: #2dbcaf;
`;

const DiscountInfo = styled.div`
  font-size: 12px;
  color: #10b981;
  margin-top: 4px;
`;

const InfoBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
`;

const InfoIcon = styled.span`
  font-size: 16px;
`;

const InfoText = styled.p`
  font-size: 12px;
  color: #92400e;
  margin: 0;
  line-height: 1.5;
`;

const SupportedApps = styled.div`
  text-align: center;
  margin-bottom: 16px;
`;

const SupportedLabel = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 8px;
`;

const AppList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
`;

const AppBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 20px;
`;

const BankingNote = styled.div`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 8px;
  font-style: italic;
`;

const StatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px;
  background: linear-gradient(135deg, rgba(45, 188, 175, 0.1), rgba(45, 188, 175, 0.05));
  border-radius: 12px;
  margin-bottom: 12px;
`;

const PulsingDot = styled.div`
  width: 10px;
  height: 10px;
  background: #2dbcaf;
  border-radius: 50%;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.5;
      transform: scale(1.2);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const StatusText = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #2dbcaf;
`;

const CountdownText = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #64748b;
  font-family: monospace;
`;

const OrderInfo = styled.div`
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  margin-bottom: 16px;

  strong {
    color: #64748b;
  }
`;

const CloseButton = styled.button`
  width: 100%;
  height: 52px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  font-family: inherit;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  background: white;
  color: #64748b;

  &:active {
    background: #f8fafc;
  }
`;

/* State boxes */
const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const StateIcon = styled.div`
  font-size: 56px;
  margin-bottom: 16px;
`;

const StateTitle = styled.h3`
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin: 0 0 8px;
`;

const StateText = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
`;

const CountdownRefresh = styled.p`
  font-size: 13px;
  color: #10b981;
  margin-top: 16px;
  font-weight: 600;
`;

const RetryButton = styled.button`
  margin-top: 20px;
  padding: 12px 32px;
  background: #2dbcaf;
  color: white;
  font-size: 14px;
  font-weight: 700;
  border: none;
  border-radius: 12px;
  cursor: pointer;

  &:active {
    transform: scale(0.98);
  }
`;

const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: #2dbcaf;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
